import React, { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import axios from 'axios';
import { Info, Check } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/constants';

const RatingMessageField = forwardRef(({ theme, disabled, sessionId, sourceActivityId, readOnly = false }, ref) => {
  const id = useId();
  const [message, setMessage] = useState('');
  const [savedMessages, setSavedMessages] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [connectionId, setConnectionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [historyError, setHistoryError] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const request = useRef(null);
  const [sentTo, setSentTo] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    let active = true;
    const params = { session_id: sessionId, source_activity_id: sourceActivityId };
    setLoading(true); setError(false); setConnectionId('');
    axios.get(`${API}/chat/activity-recipients`, { params }).then(({ data }) => {
      if (!active) return;
      setCoaches(data.participants.filter(p => p.account_type === 'coach'));
      setConnectionId(data.id);
    }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    setHistoryError(false);
    axios.get(`${API}/chat/activity-feedback`, { params }).then(({ data }) => {
      if (active) setSavedMessages(previous => [...data.messages, ...previous.filter(p => !data.messages.some(m => m.id === p.id))]);
    }).catch(() => { if (active) setHistoryError(true); });
    return () => { active = false; };
  }, [sessionId, sourceActivityId]);

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (message.trim() && !sent && !connectionId) {
        toast.error('The activity coaches could not be loaded. Your text is still here; please retry before sending.');
        return false;
      }
      return true;
    },
    send: async currentSessionId => {
      if (sent) return sentTo;
      if (!message.trim()) return '';
      const text = message.trim();
      if (!request.current || request.current.text !== text || request.current.connectionId !== connectionId) {
        request.current = { text, connectionId, request_id: crypto.randomUUID() };
      }
      try {
        const { data } = await axios.post(`${API}/chat/activity-messages`, { text, session_id: currentSessionId, request_id: request.current.request_id });
        setSavedMessages(previous => [...previous.filter(m => m.id !== data.id), data]);
        setSent(true); setSentTo(data.connection_id); setMessage('');
        return data.connection_id;
      } catch {
        toast.error('Your rating was saved, but the message was not sent. Your text is still here; try saving again.');
        throw new Error('MESSAGE_SEND_FAILED');
      }
    }
  }));

  return <div className={`p-4 rounded-2xl border mb-4 ${theme.card}`}>
    <div className="flex items-center justify-between gap-3">
      {readOnly || sent ? <h3 className={`font-semibold ${theme.textPrimary}`}>Your message</h3> : <label htmlFor={`${id}-text`} className={`font-semibold ${theme.textPrimary}`}>Message your coaches (optional)</label>}
      <button type="button" aria-label="About activity messages" aria-expanded={showInfo} aria-controls={`${id}-info`} onClick={() => setShowInfo(value => !value)} className={`p-2 rounded-full shrink-0 ${theme.button} ${theme.textMuted}`}><Info size={18} /></button>
    </div>
    <p id={`${id}-privacy`} className={`text-sm mt-1 mb-3 ${theme.textMuted}`}>Your text is not anonymous. The rest of your rating is anonymous.</p>
    {showInfo && <div id={`${id}-info`} className={`text-sm p-3 rounded-xl mb-3 ${theme.button} ${theme.textMuted}`}>
      <p>The activity creator and the co-coaches assigned to its group see your name and the activity name. Your message is added to your ongoing conversation with them; other players cannot access it. Rating scores are not included in the chat. Messages are kept for up to one year. You can edit, delete or export yours in Chat.</p>
      {!!coaches.length && <p className="mt-2">Sent to: {coaches.map(c => c.name).join(', ')}</p>}
    </div>}
    {savedMessages.map(m => <div key={m.id} className={`p-3 rounded-xl mb-3 ${theme.button}`}>
      <p className={`whitespace-pre-wrap break-words text-sm ${theme.textPrimary}`}>{m.text}</p>
      <span className={`flex items-center gap-1 text-xs mt-2 ${theme.textMuted}`}><Check size={13} />Sent</span>
    </div>)}
    {historyError && <p role="status" className={`text-xs mb-2 ${theme.textMuted}`}>Saved messages could not be loaded. Your draft stays here.</p>}
    {!readOnly && !sent && <>
      <textarea id={`${id}-text`} aria-describedby={`${id}-privacy`} disabled={disabled} maxLength={2000} rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="What would you like to share about this activity?" className={`w-full p-3 rounded-xl border ${theme.input}`} />
      <p className={`text-xs text-right ${theme.textMuted}`}>{message.length}/2000</p>
      {loading ? <p className={`text-xs mt-2 ${theme.textMuted}`}>Loading activity coaches…</p> : error && <p role="status" className={`text-xs mt-2 ${theme.textMuted}`}>The activity coaches could not be loaded. Your text stays here; you can save your rating without a message.</p>}
    </>}
  </div>;
});
RatingMessageField.displayName = 'RatingMessageField';
export default RatingMessageField;
